import { lazy, Suspense, useEffect, useState } from 'react';
import { Badge, Card, EmptyState, Icon, SectionTitle, Spinner, SURVEY_STATUS_TONE, Tabs } from '../../components';
import { ApiError } from '../../lib/api/client';
import { useI18n } from '../../lib/i18n';
import { Link, Navigate, useParams, useSearchParams } from '../../lib/router';
import type { SurveyDetail } from '../../lib/api/types';
import type { DraftRecord } from '../../lib/offline/types';
import { PageHeader } from '../../app/AppShell';
import { useDrafts, usePackSites } from '../../app/hooks';
import { useMe } from '../../app/me';
import { useServices } from '../../app/services';
import { useQueryClient } from '../../lib/query';
import { SurveyIdLabel } from '../capture/CapturePages';
import { PageFallback } from '../../app/lazy';

/** Desk screens, each its own chunk so the phone capture path stays light. */
const SurveyDetailScreen = lazy(() => import('./SurveyDetailPage').then((m) => ({ default: m.SurveyDetailPage })));
const ServerSurveyList = lazy(() => import('./ServerSurveyList').then((m) => ({ default: m.ServerSurveyList })));

const SYNC_TONE = { local: 'neutral', pending: 'info', synced: 'success', rejected: 'danger' } as const;

function DraftRow({ d, siteName }: { d: DraftRecord; siteName?: string }) {
  const { t } = useI18n();
  return (
    <Link to={`/capture/${d.id}`}>
      <Card interactive className="flex items-center gap-3 p-3">
        <Icon name="clipboard" className="shrink-0 text-brand" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{siteName ?? '—'}</p>
          <p className="truncate text-sm">
            <SurveyIdLabel draft={d} />
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge tone={SURVEY_STATUS_TONE[d.status] ?? 'neutral'}>{t(`surveyStatus.${d.status}`)}</Badge>
          {d.withdrawn ? (
            <Badge tone="warning">{t(`ui.surveys.withdrawn.${d.withdrawn.reason}`)}</Badge>
          ) : (
            <Badge tone={SYNC_TONE[d.syncState]}>{t(`ui.surveys.sync.${d.syncState}`)}</Badge>
          )}
        </div>
      </Card>
    </Link>
  );
}

/**
 * Surveys: the reporter's surveys on this device and, for users who can see
 * surveys in their scope, the server-side list ("All surveys").
 */
export function SurveysPage() {
  const { t } = useI18n();
  const { can } = useMe();
  const [params, setParams] = useSearchParams();
  const showAll = can('survey.view');
  const deviceFirst = can('survey.create');
  const tab = (params.get('tab') as 'device' | 'all' | null) ?? (deviceFirst || !showAll ? 'device' : 'all');
  return (
    <>
      <PageHeader title={t('ui.nav.surveys')} subtitle={tab === 'device' ? t('ui.surveys.onDevice') : t('ui.surveyDesk.allSubtitle')} />
      {showAll && (
        <div className="mb-4">
          <Tabs
            label={t('ui.nav.surveys')}
            value={tab}
            onChange={(v) => setParams(v === (deviceFirst ? 'device' : 'all') ? {} : { tab: v }, { replace: true })}
            tabs={
              deviceFirst
                ? [
                    { value: 'device', label: t('ui.surveyDesk.tabDevice') },
                    { value: 'all', label: t('ui.surveyDesk.tabAll') },
                  ]
                : [
                    { value: 'all', label: t('ui.surveyDesk.tabAll') },
                    { value: 'device', label: t('ui.surveyDesk.tabDevice') },
                  ]
            }
          />
        </div>
      )}
      {tab === 'all' && showAll ? (
        <Suspense fallback={<PageFallback />}>
          <ServerSurveyList />
        </Suspense>
      ) : (
        <DeviceSurveys />
      )}
    </>
  );
}

/** Surveys on this device (drafts, returned and submitted surveys). */
function DeviceSurveys() {
  const { t } = useI18n();
  const { me } = useMe();
  const drafts = useDrafts();
  const pack = usePackSites();
  const mine = (drafts.data ?? []).filter((d) => d.reporterUserId === me.user.id);
  const name = (siteId: string) => pack.data?.find((s) => s.id === siteId)?.name;
  const inProgress = mine.filter((d) => d.status === 'DRAFT' || d.status === 'RETURNED');
  const sent = mine.filter((d) => !(d.status === 'DRAFT' || d.status === 'RETURNED'));
  return (
    <>
      {mine.length === 0 && !drafts.loading && <EmptyState icon="clipboard" title={t('ui.surveys.none')} />}
      {inProgress.length > 0 && <SectionTitle>{t('ui.surveys.inProgress')}</SectionTitle>}
      <div className="flex flex-col gap-2">
        {inProgress.map((d) => (
          <DraftRow key={d.id} d={d} siteName={name(d.siteId)} />
        ))}
      </div>
      {sent.length > 0 && <SectionTitle>{t('ui.surveys.submitted')}</SectionTitle>}
      <div className="flex flex-col gap-2">
        {sent.map((d) => (
          <DraftRow key={d.id} d={d} siteName={name(d.siteId)} />
        ))}
      </div>
    </>
  );
}

/**
 * /surveys/:id — open a survey: a local draft goes straight to capture; a
 * survey returned to this reporter is brought onto the device; anything else
 * is the desk survey detail screen.
 */
export function SurveyOpenPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const { me } = useMe();
  const { drafts, api, storage } = useServices();
  const queryClient = useQueryClient();
  const [state, setState] = useState<'loading' | 'capture' | 'detail' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (await drafts.get(id)) return alive && setState('capture');
      try {
        const d = await api.get<SurveyDetail>(`/surveys/${encodeURIComponent(id)}`);
        const s = d.survey;
        const mineEditable = s.reporter_user_id === me.user.id && (s.status === 'RETURNED' || s.status === 'DRAFT');
        if (mineEditable) {
          // Editing needs the draft lock on this device (ADR-0006); taking over our own
          // survey from another device needs no extra permission.
          await api.post(`/surveys/${encodeURIComponent(id)}/lock`, { takeover: true });
          // Keep the pinned template on the device so capture works offline.
          await storage.putTemplateVersion({ versionId: d.template.versionId, templateId: d.template.id, version: null, definition: d.template.definition });
          await drafts.importSurveyRow(s);
          return alive && setState('capture');
        }
        // Reuse the response for the detail screen instead of fetching it again.
        queryClient.setQueryData(['surveys', id], d);
        if (alive) setState('detail');
      } catch (e) {
        if (!alive) return;
        setError(e instanceof ApiError ? e.code : 'UNKNOWN');
        setState('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, api, drafts, storage, me.user.id, queryClient]);

  if (state === 'loading')
    return (
      <div className="flex justify-center py-20 text-brand">
        <Spinner size={32} />
      </div>
    );
  if (state === 'capture') return <Navigate to={`/capture/${id}`} replace />;
  if (state === 'error') return <EmptyState icon="alert" title={t('ui.common.loadFailed')} description={error ?? undefined} />;
  return (
    <Suspense fallback={<PageFallback />}>
      <SurveyDetailScreen id={id} />
    </Suspense>
  );
}
